import { Button, cn, ConfirmationDialog, toast } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotOverviewMediaFile } from '@/api/rest/resources/types/plot';
import { useRemovePlotOverviewMediaFile } from '@/hooks/usePlotOverviewMediaFile';

import { MediaFileDialog } from './MediaFileDialog';

const BUTTON_STYLES = 'px-3 rounded-xl';

type ActionButtonProps = {
  mediaFileId: string;
  className?: string;
};

export const DeleteMediaFileButton = ({ className, mediaFileId }: ActionButtonProps) => {
  const { t } = useTranslation();
  const { submit, isSubmitting } = useRemovePlotOverviewMediaFile();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

  const handleConfirmDelete = async () => {
    setIsConfirmationDialogOpen(false);
    await submit(
      mediaFileId,
      () => {
        triggerNotification(
          t('shared.uploadsPlotOverview.plotOverview.form.toast.mediaFile.success.delete'),
          'success',
        );
      },
      () =>
        triggerNotification(t('shared.uploadsPlotOverview.plotOverview.form.toast.mediaFile.error.delete'), 'error'),
    );
  };

  return (
    <>
      <Button
        className={cn('bg-error hover:bg-error-dark', BUTTON_STYLES, className)}
        onClick={() => setIsConfirmationDialogOpen(true)}
        loading={isSubmitting}
        data-testid='delete-media-file-button'
      >
        <i className='ri-delete-bin-line' />
      </Button>
      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        CancelAction={
          <Button onClick={() => setIsConfirmationDialogOpen(false)} variant='outline'>
            {t('global.ui.buttons.cancel')}
          </Button>
        }
        PrimaryAction={
          <Button className='bg-error hover:bg-error-dark' onClick={handleConfirmDelete}>
            {t('global.ui.buttons.delete')}
          </Button>
        }
        header={t('shared.uploadsPlotOverview.plotOverview.labels.confirmDeleteHeder')}
        body={t('shared.uploadsPlotOverview.plotOverview.labels.confirmDeleteBody')}
      />
    </>
  );
};

export const EditMediaFileButton = ({
  className,
  mediaFile,
  plotOverviewId,
}: {
  className?: string;
  mediaFile: PlotOverviewMediaFile;
  plotOverviewId: string;
}) => {
  const [isMediaFileDialogOpen, setIsMediaFileDialogOpen] = useState(false);

  return (
    <>
      <Button
        className={cn(BUTTON_STYLES, className)}
        data-testid='edit-media-file-button'
        onClick={() => setIsMediaFileDialogOpen(true)}
      >
        <i className='ri-pencil-fill' />
      </Button>
      <MediaFileDialog
        open={isMediaFileDialogOpen}
        onClose={() => setIsMediaFileDialogOpen(false)}
        type='update'
        mediaFile={mediaFile}
        plotOverviewId={plotOverviewId}
      />
    </>
  );
};

const triggerNotification = (message: string, type: 'success' | 'error') => {
  toast({
    title: message,
    type,
  });
};
